import {
  AccessTokenResponseSchema,
  type CurrentAdminUser,
  CurrentAdminUserSchema,
} from '@sbc/domain-models/admin';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { apiSend, registerRefreshHandler, setAccessToken } from '@/lib/api/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  user: CurrentAdminUser | null;
  // True until the initial silent-refresh attempt resolves, so guards don't bounce
  // a returning user to /login before their session is restored.
  isBootstrapping: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchCurrentUser(accessToken: string): Promise<CurrentAdminUser | null> {
  const response = await fetch('/api/auth/me', {
    headers: { accept: 'application/json', authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  });
  if (response.status === 401 || response.status === 403) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`GET /api/auth/me failed: ${response.status}`);
  }
  return CurrentAdminUserSchema.parse(await response.json());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CurrentAdminUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const sessionRef = useRef<CurrentAdminUser | null>(null);

  const applySession = useCallback((token: string | null, user: CurrentAdminUser | null) => {
    setAccessToken(user ? token : null);
    sessionRef.current = user;
    setSession(user);
  }, []);

  // Raw refresh against the httpOnly cookie — deliberately bypasses the client
  // interceptor so a 401 here can't recurse. Returns whether a session was restored.
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        applySession(null, null);
        return false;
      }
      const { accessToken } = AccessTokenResponseSchema.parse(await response.json());
      const user = await fetchCurrentUser(accessToken).catch(() => sessionRef.current);
      applySession(accessToken, user);
      return user !== null;
    } catch {
      applySession(null, null);
      return false;
    }
  }, [applySession]);

  // Keep `refresh` current for the interceptor without re-registering each render.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    registerRefreshHandler(() => refreshRef.current());
  }, []);

  // On boot, attempt a silent refresh to restore a session across reloads.
  useEffect(() => {
    void refresh().finally(() => setIsBootstrapping(false));
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const { accessToken } = await apiSend(
        'POST',
        '/api/auth/login',
        { email, password },
        AccessTokenResponseSchema,
      );
      const user = await fetchCurrentUser(accessToken);
      if (!user) {
        throw new Error('Could not load your account.');
      }
      applySession(accessToken, user);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      applySession(null, null);
    }
  }, [applySession]);

  const value = useMemo<AuthContextValue>(
    () => ({ isAuthenticated: session !== null, user: session, isBootstrapping, login, logout }),
    [session, isBootstrapping, login, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
